import { CurrencyEnum, HeadlessUI, centsToUnits, ArrowDownIcon, CloseIcon } from '@systemeio/ui'
import { useAffiliateDashboardVendors } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-vendors'
import { AffiliateDashboardFilterInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-interface'
import {
  AffiliateDashboardVendorsInterface,
  VendorId,
} from 'modules/affiliate-dashboard/types/affiliate-dashboard-vendors-interface'
import React, { Fragment, useEffect, useState } from 'react'
import { Loader } from 'shared/components/loader'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { currencyFormat } from 'shared/utils/currency-format'

interface AffiliateVendorSelectorProps {
  selectedVendor?: VendorId
  setFilter: React.Dispatch<React.SetStateAction<AffiliateDashboardFilterInterface>>
}

const AffiliateDashboardVendorSelector = ({
  selectedVendor,
  setFilter,
}: AffiliateVendorSelectorProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const [shouldFetch, setShouldFetch] = useState(false)

  useEffect(() => {
    if (selectedVendor) {
      setShouldFetch(true)
    }
  }, [selectedVendor])

  const { data: vendors } = useAffiliateDashboardVendors(shouldFetch)

  const selectedVendorName = vendors?.find(el => el.vendor.id === selectedVendor)?.vendor
    .affiliateProgramBrandName

  const columns: ColumnDefinitionInterface<AffiliateDashboardVendorsInterface>[] = [
    {
      key: 'vendor',
      header: t('dashboard.affiliate_dashboard.vendor'),
      widthPercent: 30,
      onRender: vendor => (
        <div className={'w-full truncate'}>
          <span
            className={`${
              vendor.vendor.id === selectedVendor ? 'text-brand-primary' : 'text-text-primary'
            } w-full truncate`}
          >
            {vendor.vendor.affiliateProgramBrandName}
          </span>
        </div>
      ),
    },
    {
      key: 'sales',
      header: t('dashboard.affiliate.lifetime_earnings'),
      widthPercent: 30,
      onRender: vendor => (
        <div className={'flex flex-col gap-1.5'}>
          {Object.keys(vendor.sales).map((el, idx) => {
            const cur = el as CurrencyEnum
            return (
              <span
                className={`${
                  vendor.vendor.id === selectedVendor ? 'text-brand-primary' : 'text-text-primary'
                } `}
                key={idx}
              >
                {currencyFormat(centsToUnits(vendor.sales[cur]), cur, user?.dashboardLocale)}
              </span>
            )
          })}
        </div>
      ),
    },
    {
      key: 'sales',
      widthPercent: 40,
      header: (
        <div className={'flex gap-1'}>
          <span className={'text-text-primary'}>{`${t('dashboard.affiliate.paid')} /`}</span>
          <span className={'text-text-primary/50'}>{t('dashboard.affiliate.pending')}</span>
        </div>
      ),
      onRender: vendor => (
        <div className={'flex flex-col gap-1.5'}>
          {Object.keys(vendor.paid).map((el, idx) => {
            const cur = el as CurrencyEnum
            return (
              <div key={idx} className={'flex gap-1'}>
                <span
                  className={`${
                    vendor.vendor.id === selectedVendor ? 'text-brand-primary' : 'text-text-primary'
                  } `}
                >
                  {`${currencyFormat(
                    centsToUnits(vendor.paid[cur]),
                    cur,
                    user?.dashboardLocale,
                  )} /`}
                </span>
                <span
                  className={`${
                    vendor.vendor.id === selectedVendor
                      ? 'text-brand-primary/50'
                      : 'text-text-primary/50'
                  } `}
                >
                  {currencyFormat(centsToUnits(vendor.pending[cur]), cur, user?.dashboardLocale)}
                </span>
              </div>
            )
          })}
        </div>
      ),
    },
  ]

  return (
    <HeadlessUI.Popover className="relative w-full">
      {({ open }) => (
        <>
          <HeadlessUI.PopoverButton
            className={`flex w-full items-center justify-between gap-3 truncate py-2.5 pl-4 lg:min-w-[200px] ${
              selectedVendor ? 'pr-16' : 'pr-10'
            } bg-background-secondary text-text-primary main-transition-colors rounded-lg border text-left text-sm ${
              open ? 'border-brand-primary' : 'border-text-secondary/30'
            } ${
              open && 'border-brand-primary'
            } focus-visible:border-brand-primary text-text-primary placeholder:text-text-tertiary/70 focus:outline-hidden`}
          >
            {selectedVendor ? (
              selectedVendorName ? (
                <span className={'truncate'}>{selectedVendorName}</span>
              ) : (
                <Loader small />
              )
            ) : (
              <span>{t('dashboard.affiliate_dashboard.all_vendors')}</span>
            )}
            {selectedVendor && (
              <div
                onClick={e => {
                  e.stopPropagation()
                  setFilter(prev => ({ ...prev, vendorId: undefined }))
                }}
                className={`absolute inset-y-0 right-0 flex items-center pr-10`}
              >
                <CloseIcon className={`fill-text-primary cursor-pointer`} />
              </div>
            )}
            <div className="absolute inset-y-0 right-0 flex items-center pr-4">
              <ArrowDownIcon
                className={`stroke-text-primary cursor-pointer transition-transform duration-300 ${
                  open && 'rotate-180'
                }`}
                aria-hidden="true"
              />
            </div>
          </HeadlessUI.PopoverButton>
          <HeadlessUI.Transition
            beforeEnter={() => setShouldFetch(true)}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <HeadlessUI.PopoverPanel className="absolute right-0 z-10 mt-1.5 w-screen max-w-sm transform px-4 sm:px-0 lg:max-w-3xl">
              {({ close }) => (
                <div className="bg-background-secondary customScroll max-h-[calc(100vh-350px)] overflow-hidden  overflow-y-auto rounded-lg shadow-lg ring-1 ring-black/5 sm:max-h-[calc(100vh-200px)]">
                  <Table
                    nestedId={'vendor'}
                    onRowClick={vendor => {
                      setFilter(prev => ({ ...prev, vendorId: vendor.vendor.id }))
                      close()
                    }}
                    data={vendors && { hasMore: false, items: vendors }}
                    columns={columns}
                  />
                </div>
              )}
            </HeadlessUI.PopoverPanel>
          </HeadlessUI.Transition>
        </>
      )}
    </HeadlessUI.Popover>
  )
}

export default AffiliateDashboardVendorSelector
